import { t } from '../i18n/index.js';
import { outbox } from './sync.js';
import { dismiss, toast } from './toast.js';

/**
 * Briefing „Arbeitsfreude“ AF11/AF12: Rückmeldung mit Bedeutung und Rückgängig als geprüfte Gegenoperation.
 *
 * - Eine Aktion meldet, was sie bewirkt hat („Termin geändert: morgen“), und bietet – nur wenn es einen sicheren
 *   Rückweg gibt – „Rückgängig“ an. Dasselbe per Strg/⌘+Z, solange der Hinweis steht.
 * - Ist die Änderung noch nicht gesendet, wird sie im Ausgangskorb zurückgenommen. Sonst wartet der Rückweg auf die
 *   Bestätigung und läuft als NEUE Änderung mit Versionsprüfung; Felder, die inzwischen jemand anderes geändert hat,
 *   bleiben stehen und die Rückmeldung sagt das. Keine pauschale Wiederherstellung alter Snapshots.
 * - Solange offline gewartet wird, sagt der Text das („wird gespeichert, sobald du online bist“) – lokale Wirkung
 *   wird nicht als gespeichert ausgegeben.
 */

/** Ergebnis eines Rückwegs: null = vollständig zurückgenommen; sonst eine Erklärung (Konflikt, nicht mehr möglich) */
export type Revert = () => Promise<string | null>;

export interface UndoStep {
  /** opId der ursprünglichen Änderung (für die Rücknahme vor dem Senden) */
  opId?: string;
  /** Bestätigung der ursprünglichen Änderung */
  done: Promise<unknown>;
  revert: Revert;
}

let last: { toastId: number; run: () => void } | null = null;

/** Offline-Zusatz: lokale Wirkung ist noch nicht gespeichert */
export function savedNote(text: string): string {
  return outbox.getStatus().waiting ? `${text} ${t('feedback.offline')}` : text;
}

/** Rückmeldung mit Rückgängig anbieten. Schritte ohne sicheren Rückweg gar nicht erst übergeben. */
export function offerUndo(text: string, steps: UndoStep[], opts: { ms?: number } = {}): void {
  if (!steps.length) {
    toast(savedNote(text));
    return;
  }
  let used = false;
  const run = () => {
    if (used) return;
    used = true;
    if (last?.toastId === id) last = null;
    void undoSteps(steps);
  };
  const id = toast(savedNote(text), { action: { label: t('common.undo'), run }, ms: opts.ms ?? 8000 });
  last = { toastId: id, run };
  // Abnahme 0.49 F01: die Rückmeldung hängt am Ergebnis – wird die Änderung abgelehnt (Konflikt, Fehler), verschwindet
  // „… geändert“ samt Rückgängig; die Fehlermeldung des Ausgangskorbs erklärt dann, was gilt
  void Promise.allSettled(steps.map((s) => s.done)).then((r) => {
    const failed = r.filter((x) => x.status === 'rejected').length;
    if (used || !failed) return;
    dismiss(id);
    if (last?.toastId === id) last = null;
    // ADR 0170 (W3): einzelne abgewiesen (z. B. Voraussetzung des Status fehlt) – keine Meldung „alles erledigt“,
    // sondern ehrlich, wie viele gelten; Rückgängig bleibt für die übernommenen
    if (failed < r.length) {
      const id2 = toast(t('undo.partial', { ok: r.length - failed, n: r.length }), { kind: 'error', action: { label: t('common.undo'), run }, ms: opts.ms ?? 10_000 });
      last = { toastId: id2, run };
    }
  });
  setTimeout(() => {
    if (last?.toastId === id) last = null;
  }, opts.ms ?? 8000);
}

async function undoSteps(steps: UndoStep[]) {

  const notes: string[] = [];
  for (const s of steps) {
    // Noch nicht gesendet: einfach zurücknehmen (Queue-Regel)
    if (s.opId && outbox.cancel(s.opId)) continue;
    try {
      await s.done;
    } catch {
      continue; // ursprüngliche Änderung ist gescheitert – nichts zurückzunehmen
    }
    try {
      const note = await s.revert();
      if (note) notes.push(note);
    } catch (err) {
      notes.push(err instanceof Error ? err.message : t('undo.failed'));
    }
  }
  if (notes.length) toast(notes.length === 1 ? notes[0]! : t('undo.partly', { n: notes.length }), { kind: 'error', ms: 10_000 });
}

/** Strg/⌘+Z: letzte angebotene Rücknahme – nicht in Textfeldern (dort gilt das Rückgängig des Browsers) */
function editing(el: Element | null): boolean {
  if (!el) return false;
  const h = el as HTMLElement;
  return h.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (!last || e.key.toLowerCase() !== 'z' || !(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
    if (editing(document.activeElement)) return;
    e.preventDefault();
    const l = last;
    last = null;
    dismiss(l.toastId);
    l.run();
  });
}
